<template>
  <div>
    <b-row>
      <b-col md="6">
        <b-form-group label-for="tanggal-wawancara">
          <template #label>Tanggal Wawancara <span class="text-danger">*</span></template>
          <b-form-input
            id="tanggal-wawancara"
            :value="value.tanggal_wawancara"
            type="date"
            @input="updateField('tanggal_wawancara', $event)"
          />
        </b-form-group>
      </b-col>
      <b-col md="6">
        <b-form-group label="Waktu Wawancara" label-for="waktu-mulai-wawancara">
          <b-row>
            <b-col>
              <b-form-input
                id="waktu-mulai-wawancara"
                :value="value.waktu_mulai"
                type="time"
                aria-label="Waktu mulai wawancara"
                @input="updateField('waktu_mulai', $event)"
              />
              <small class="text-muted">Mulai <span class="text-danger">*</span></small>
            </b-col>
            <b-col>
              <b-form-input
                id="waktu-selesai-wawancara"
                :value="value.waktu_selesai"
                type="time"
                aria-label="Waktu selesai wawancara"
                @input="updateField('waktu_selesai', $event)"
              />
              <small class="text-muted">Selesai <span class="text-danger">*</span></small>
            </b-col>
            <b-col>
              <b-form-select
                id="zona-waktu-wawancara"
                :value="value.zona_waktu"
                :options="zonaWaktuOptions"
                class="form-control"
                @change="updateField('zona_waktu', $event)"
              />
              <small class="text-muted">Zona Waktu <span class="text-danger">*</span></small>
            </b-col>
          </b-row>
          <small v-if="invalidTimeRange" class="text-danger">Waktu selesai harus lebih besar dari waktu mulai.</small>
        </b-form-group>
      </b-col>
      <b-col md="6">
        <b-form-group label-for="lokasi-wawancara">
          <template #label>Lokasi <span class="text-danger">*</span></template>
          <b-form-textarea
            id="lokasi-wawancara"
            :value="value.lokasi"
            rows="3"
            @input="updateField('lokasi', $event)"
          />
        </b-form-group>
      </b-col>
      <b-col md="6">
        <b-form-group label-for="pewawancara">
          <template #label>Pewawancara <span class="text-danger">*</span></template>
          <v-select
            id="pewawancara"
            :value="value.pewawancara"
            :options="timPenilaiOptions"
            :reduce="(penilai) => penilai.id"
            label="nama"
            placeholder="Pilih Tim Penilai"
            :disabled="isLoadingTimPenilai"
            @input="updateField('pewawancara', $event)"
          />
          <small v-if="isLoadingTimPenilai" class="text-muted">Memuat daftar Tim Penilai...</small>
          <small v-else-if="timPenilaiError" class="text-danger">{{ timPenilaiError }}</small>
        </b-form-group>
      </b-col>
    </b-row>

    <section class="mt-3 mb-4">
      <h5>Identitas Narasumber</h5>
      <b-row>
        <b-col md="6">
          <b-form-group label-for="nama-narasumber">
            <template #label>Nama <span class="text-danger">*</span></template>
            <b-form-input
              id="nama-narasumber"
              :value="value.nama_narasumber"
              type="text"
              @input="updateField('nama_narasumber', $event)"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label-for="alamat-narasumber">
            <template #label>Alamat <span class="text-danger">*</span></template>
            <b-form-textarea
              id="alamat-narasumber"
              :value="value.alamat_narasumber"
              rows="3"
              @input="updateField('alamat_narasumber', $event)"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label-for="no-surat-tugas-kuasa">
            <template #label>No. Surat Tugas/Kuasa (dalam hal mewakili) <span class="text-muted">(Opsional)</span></template>
            <b-form-input
              id="no-surat-tugas-kuasa"
              :value="value.no_surat_tugas_kuasa"
              type="text"
              @input="updateField('no_surat_tugas_kuasa', $event)"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </section>

    <section class="mb-4">
      <h5>Tabel Wawancara</h5>
      <b-row class="d-none d-md-flex font-weight-bold border-bottom pb-2 mb-2">
        <b-col md="2">Indikator</b-col>
        <b-col md="4">Pertanyaan</b-col>
        <b-col md="3">Jawaban <span class="text-danger">*</span></b-col>
        <b-col md="3">Penjelasan <span class="text-danger">*</span></b-col>
      </b-row>
      <template v-for="group in questionGroups">
        <b-row
          v-for="(question, questionIndex) in group.questions"
          :key="question.id"
          class="border-bottom py-3"
        >
          <b-col v-if="questionIndex === 0" cols="12" class="d-md-none mb-2">
            <strong>{{ group.label }}</strong>
          </b-col>
          <b-col md="2" class="d-none d-md-block">
            <strong v-if="questionIndex === 0">{{ group.label }}</strong>
          </b-col>
          <b-col cols="12" md="4" class="mb-2 mb-md-0">
            {{ question.text }}
          </b-col>
          <b-col cols="12" md="3" class="mb-2 mb-md-0">
            <label :for="`jawaban-${question.id}`" class="d-md-none">
              Jawaban <span class="text-danger">*</span>
            </label>
            <b-form-select
              v-if="group.answerType === 'conformity'"
              :id="`jawaban-${question.id}`"
              :value="getQuestionAnswer(question.id, 'jawaban')"
              :options="conformityOptions"
              class="form-control"
              :aria-label="`Jawaban: ${question.text}`"
              @change="updateQuestionAnswer(question.id, 'jawaban', $event)"
            />
            <b-form-textarea
              v-else-if="group.answerType === 'textarea'"
              :id="`jawaban-${question.id}`"
              :value="getQuestionAnswer(question.id, 'jawaban')"
              rows="2"
              :aria-label="`Jawaban: ${question.text}`"
              @input="updateQuestionAnswer(question.id, 'jawaban', $event)"
            />
            <b-form-select
              v-else
              :id="`jawaban-${question.id}`"
              :value="getQuestionAnswer(question.id, 'jawaban')"
              :options="completionOptions"
              class="form-control"
              :aria-label="`Jawaban: ${question.text}`"
              @change="updateQuestionAnswer(question.id, 'jawaban', $event)"
            />
          </b-col>
          <b-col cols="12" md="3">
            <label :for="`penjelasan-${question.id}`" class="d-md-none">
              Penjelasan <span class="text-danger">*</span>
            </label>
            <b-form-textarea
              :id="`penjelasan-${question.id}`"
              :value="getQuestionAnswer(question.id, 'penjelasan')"
              rows="2"
              :aria-label="`Penjelasan: ${question.text}`"
              @input="updateQuestionAnswer(question.id, 'penjelasan', $event)"
            />
          </b-col>
        </b-row>
      </template>
    </section>

    <b-form-group label-for="catatan-wawancara" class="mb-4">
      <template #label>Catatan <span class="text-danger">*</span></template>
      <ckeditor
        id="catatan-wawancara"
        :editor="wygEditor"
        :value="value.catatan || ''"
        @input="updateField('catatan', $event)"
      />
    </b-form-group>

    <div class="d-flex justify-content-between">
      <b-button variant="secondary" @click="$emit('kembali')">Kembali</b-button>
      <div>
        <b-button variant="success" class="mr-2" @click="$emit('simpan')">Simpan</b-button>
        <b-button variant="primary" @click="$emit('selanjutnya')">Selanjutnya</b-button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import ClassicEditor from '@ckeditor/ckeditor5-build-classic'
import vSelect from 'vue-select'
import { createEmptyInterviewAnswers, wawancaraQuestionGroups } from './wawancaraQuestions'

export default {
  name: 'FormWawancaraUpt',
  components: { vSelect },
  props: {
    value: {
      type: Object,
      default: () => ({
        tanggal_wawancara: '',
        waktu_mulai: '',
        waktu_selesai: '',
        zona_waktu: 'WIB',
        lokasi: '',
        pewawancara: null,
        nama_narasumber: '',
        alamat_narasumber: '',
        no_surat_tugas_kuasa: '',
        jawaban_pertanyaan: createEmptyInterviewAnswers(),
        catatan: '',
      }),
    },
  },
  data() {
    return {
      baseapiPengendalian: localStorage.getItem('baseapiPengendalian'),
      user: JSON.parse(localStorage.getItem('userPengendalian') || '{}'),
      wygEditor: ClassicEditor,
      zonaWaktuOptions: [
        { value: 'WIB', text: 'WIB' },
        { value: 'WITA', text: 'WITA' },
        { value: 'WIT', text: 'WIT' },
      ],
      questionGroups: wawancaraQuestionGroups,
      conformityOptions: [
        { value: '', text: 'Pilih Jawaban' },
        { value: 'Sesuai', text: 'Sesuai' },
        { value: 'Tidak Sesuai', text: 'Tidak Sesuai' },
      ],
      completionOptions: [
        { value: '', text: 'Pilih Jawaban' },
        { value: 'Sudah', text: 'Sudah' },
        { value: 'Belum Dilaksanakan', text: 'Belum Dilaksanakan' },
      ],
      timPenilaiOptions: [],
      isLoadingTimPenilai: false,
      timPenilaiError: '',
    }
  },
  computed: {
    invalidTimeRange() {
      return Boolean(
        this.value.waktu_mulai && this.value.waktu_selesai && this.value.waktu_selesai <= this.value.waktu_mulai,
      )
    },
  },
  mounted() {
    this.getTimPenilai()
  },
  methods: {
    updateField(field, fieldValue) {
      this.$emit('input', { ...this.value, [field]: fieldValue })
    },
    getQuestionAnswer(questionId, field) {
      const answer = (this.value.jawaban_pertanyaan || {})[questionId]
      return answer ? answer[field] : ''
    },
    updateQuestionAnswer(questionId, field, fieldValue) {
      const currentAnswers = this.value.jawaban_pertanyaan || {}
      const currentAnswer = currentAnswers[questionId] || { jawaban: '', penjelasan: '' }
      this.$emit('input', {
        ...this.value,
        jawaban_pertanyaan: {
          ...currentAnswers,
          [questionId]: { ...currentAnswer, [field]: fieldValue },
        },
      })
    },
    async getTimPenilai() {
      this.isLoadingTimPenilai = true
      this.timPenilaiError = ''
      try {
        const response = await axios.get(
          `${this.baseapiPengendalian}api/user?filter=roles,contains,Verifikator;nama_upt,!=,PUSAT&limit=0`,
          { headers: { Authorization: `Bearer ${this.user.accessToken}` } },
        )
        this.timPenilaiOptions = response.data.data || []
      } catch (error) {
        this.timPenilaiError = 'Daftar Tim Penilai gagal dimuat.'
      } finally {
        this.isLoadingTimPenilai = false
      }
    },
  },
}
</script>
